import React, { useEffect, useState, useCallback, memo, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import MessageModal from "../../components/common/MessageModal";
import CONFIG from "../../config";

const MessagesAdmin = memo(() => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [page, setPage] = useState(1);
  const [per] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeMessage, setActiveMessage] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const isArabic = i18n.language?.startsWith('ar');

  const headers = useMemo(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  // fetch messages (admin)
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/contact/admin`, {
        params: { q: query, field: searchField, page, per },
        headers
      });
      setMessages(res.data.data || []);
      setTotal(res.data.meta?.total ?? res.data.total ?? 0);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) alert(t('admin.common.access_denied', 'Access Denied'));
    } finally { setLoading(false); }
  }, [headers, query, searchField, page, per, t]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // Poll unread count (10s)
  useEffect(() => {
    let interval;
    async function fetchUnread() {
      try {
        const res = await axios.get(`${CONFIG.API_URL}/api/contact/admin/unread-count`, { headers });
        setUnreadCount(res.data.unread ?? 0);
      } catch (e) { console.warn(e); }
    }
    fetchUnread();
    interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, [headers]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleSelectAllOnPage = () => {
    const allOnPageSelected = messages.length > 0 && messages.every(m => selected.has(m.id));
    const s = new Set(selected);
    if (allOnPageSelected) messages.forEach(m => s.delete(m.id));
    else messages.forEach(m => s.add(m.id));
    setSelected(s);
  };

  const clearSelection = () => setSelected(new Set());

  const bulkAction = async (action) => {
    if (selected.size === 0) return alert(t('admin.messages_page.select_first', 'Select messages first'));
    const ids = Array.from(selected);
    try {
      if (action === "delete") await axios.post(`${CONFIG.API_URL}/api/contact/admin/bulk-delete`, { ids }, { headers });
      else if (action === "mark-read") await axios.post(`${CONFIG.API_URL}/api/contact/admin/mark-read`, { ids }, { headers });
      else if (action === "mark-unread") await axios.post(`${CONFIG.API_URL}/api/contact/admin/mark-unread`, { ids }, { headers });

      clearSelection();
      fetchMessages();
    } catch (err) {
      console.error(err);
      alert(t('admin.common.operation_failed', 'Action failed'));
    }
  };

  const openMessage = async (id) => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/contact/admin/${id}`, { headers });
      setActiveMessage(res.data);
      setModalOpen(true);
      if (!res.data.is_read) {
        await axios.post(`${CONFIG.API_URL}/api/contact/admin/mark-read`, { ids: [id] }, { headers });
        fetchMessages();
      }
    } catch (err) { console.error(err); }
  };

  const onMarkReadToggle = async (message) => {
    try {
      if (message.is_read) await axios.post(`${CONFIG.API_URL}/api/contact/admin/mark-unread`, { ids: [message.id] }, { headers });
      else await axios.post(`${CONFIG.API_URL}/api/contact/admin/mark-read`, { ids: [message.id] }, { headers });
      setModalOpen(false);
      fetchMessages();
    } catch (err) { console.error(err); }
  };

  const replyTo = (msg) => {
    if (!msg?.email) return alert(t('admin.messages_page.no_email', 'No email address available to reply.'));
    const subj = encodeURIComponent(`Re: ${msg.subject || t('admin.messages_page.default_subject', 'your message')}`);
    const body = encodeURIComponent(`\n\n---\n${t('admin.messages_page.original_message', 'Original message')}:\n${msg.message || ""}`);
    window.location.href = `mailto:${msg.email}?subject=${subj}&body=${body}`;
  };

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
      <div className="flex justify-between items-center mb-12">
        <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.messages_page.title')}</h2>
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-gray-500 hover:text-brand-primary transition-all text-sm font-medium group"
          >
            <span className={`transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
            {t('admin.common.back', 'Back')}
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-col xl:flex-row gap-4 xl:items-center bg-gray-50 p-4 rounded-[30px] border border-gray-100">
        <div className="relative flex-1 w-full flex flex-col md:flex-row gap-2">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="p-3 rounded-2xl border-none bg-white shadow-sm focus:ring-2 focus:ring-brand-primary/20 font-bold text-sm text-gray-600 w-full md:w-auto"
          >
            <option value="all">{t('admin.admin_messages_page.search_options.all')}</option>
            <option value="id">{t('admin.admin_messages_page.search_options.id')}</option>
            <option value="name">{t('admin.admin_messages_page.search_options.name')}</option>
            <option value="email">{t('admin.admin_messages_page.search_options.email')}</option>
            <option value="phone">{t('admin.admin_messages_page.search_options.phone')}</option>
          </select>
          <div className="relative flex-1">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('admin.common.search_placeholder', 'Search...')} className="w-full pl-12 pr-4 py-3 rounded-2xl border-none bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium" />
          </div>
          <button onClick={() => { setPage(1); fetchMessages(); }} className="px-6 py-3 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-primary/20 hover:scale-105 transition-all">{t('admin.common.search')}</button>
        </div>

        {unreadCount > 0 && (
          <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-xs font-bold border border-red-100 flex items-center animate-bounce-subtle">
            <span className="w-2 h-2 bg-red-600 rounded-full mr-2 animate-pulse"></span>
            {t('admin.messages_page.unread_count', { count: unreadCount })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-8 bg-white border border-gray-100 p-2 rounded-2xl w-fit">
        <button onClick={() => toggleSelectAllOnPage()} className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${messages.length > 0 && messages.every(m => selected.has(m.id)) ? "bg-brand-primary text-white shadow-lg shadow-brand-primary/20" : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}>
          {messages.length > 0 && messages.every(m => selected.has(m.id)) ? t('admin.common.deselect_all', 'Deselect All') : t('admin.common.select_all')}
        </button>
        <button onClick={() => bulkAction("mark-read")} className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-green-50 text-green-600 hover:bg-green-100 transition-all">{t('admin.messages_page.mark_read')}</button>
        <button onClick={() => bulkAction("mark-unread")} className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-amber-50 text-amber-600 hover:bg-amber-100 transition-all">{t('admin.messages_page.mark_unread')}</button>
        <button onClick={() => bulkAction("delete")} className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-red-50 text-red-600 hover:bg-red-100 transition-all">{t('admin.common.delete')}</button>
        {selected.size > 0 && <button onClick={() => clearSelection()} className="px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all">{t('admin.common.clear')}</button>}
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500 font-medium animate-pulse">{t('admin.common.loading')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {messages.map(m => (
              <div key={m.id} className={`group border border-gray-100 p-6 rounded-[24px] hover:shadow-lg hover:border-brand-primary/20 transition-all duration-300 relative ${selected.has(m.id) ? "bg-brand-primary/5 ring-2 ring-brand-primary/20" : "bg-white"}`}>
                <div className="absolute top-4 right-4">
                  <span className="px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest bg-gray-100 text-gray-500">
                    #{m.id}
                  </span>
                </div>
                <div className="flex justify-between items-start gap-3 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} className="w-5 h-5 accent-brand-primary rounded-lg cursor-pointer" />
                      <h3 className={`font-bold text-gray-900 line-clamp-1 ${!m.is_read ? "text-brand-primary" : ""}`}>{m.name}</h3>
                      {!m.is_read && <span className="text-[10px] text-white bg-red-500 px-2 py-0.5 rounded-full uppercase font-black tracking-widest shadow-sm shadow-red-500/20">{t('admin.messages_page.new')}</span>}
                    </div>
                    <p className="text-xs font-medium text-gray-500 flex items-center gap-2">
                      <span>{m.email}</span>
                      {m.phone && <span className="bg-gray-100 px-2 py-0.5 rounded text-gray-600">{m.phone}</span>}
                    </p>
                  </div>

                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest bg-gray-50 px-2 py-1 rounded-lg">
                    {new Date(m.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="bg-gray-50/50 p-4 rounded-xl border border-gray-50 mb-6 flex-1">
                  <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed font-medium">{m.subject ? <strong className="block text-gray-900 mb-1">{m.subject}</strong> : null}{m.message}</p>
                </div>

                <div className="flex gap-2 mt-auto pt-4 border-t border-gray-100 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => openMessage(m.id)} className={`flex-1 py-3 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-brand-primary/10 hover:scale-[1.02] active:scale-95 transition-all`}>
                    {t('admin.common.view')}
                  </button>
                  <button onClick={() => replyTo(m)} className="px-4 bg-gray-100 text-gray-700 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-gray-200 transition-all" title={t('admin.messages_page.reply', 'Reply')}>
                    <i className="fas fa-reply"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {total > per && (
            <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-100">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t('admin.messages_page.results', { count: total })}</div>
              <div className="flex gap-4 items-center">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold"
                >
                  {isArabic ? "→" : "←"}
                </button>
                <div className="text-xs font-black uppercase tracking-widest text-gray-400">
                  {t('admin.common.page', { current: page, total: Math.ceil(total / per) || 1 })}
                </div>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= Math.ceil(total / per)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center disabled:opacity-50 transition-all font-bold"
                >
                  {isArabic ? "←" : "→"}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <MessageModal
        open={modalOpen}
        message={activeMessage}
        onClose={() => setModalOpen(false)}
        onMarkReadToggle={onMarkReadToggle}
      />
    </div>
  );
});

export default MessagesAdmin;
